import {ArrowDownUp, Plus, Search, Star, TrendingUp} from 'lucide-react'
import { useEffect, useMemo, useState } from 'react'
import { Link } from 'react-router-dom'
import Layout from '@/components/Layout'
import ProjectCard from '@/components/ProjectCard'
import SeoHead from '@/components/SeoHead'
import { useSimPresales } from '@/hooks/useSimPresales'
import { useWatchlist } from '@/hooks/useWatchlist'
import { prefetchProjectLinks } from '@/lib/projectLinks'
import { projectCategories } from '@/lib/mockData'
import type { ProjectCategory } from '@/lib/mockData'
import { listPresaleProjects } from '@/lib/presale/listings'

type StatusFilter = 'all' | 'live' | 'upcoming' | 'success' | 'failed'
type SortOption = 'trending' | 'most-raised' | 'ending-soon'
type CategoryFilter = 'all' | ProjectCategory

const statusFilters: { value: StatusFilter, label: string }[] = [
  { value: 'all', label: 'All' },
  { value: 'live', label: 'Live' },
  { value: 'upcoming', label: 'Upcoming' },
  { value: 'success', label: 'Funded' },
  { value: 'failed', label: 'Failed' },
]

const sortOptions: { value: SortOption, label: string }[] = [
  { value: 'trending', label: 'Trending' },
  { value: 'most-raised', label: 'Most raised' },
  { value: 'ending-soon', label: 'Ending soon' },
]

function Presale() {
  const { tick } = useSimPresales()
  const allProjects = useMemo(() => listPresaleProjects(), [tick])
  const { ids: watchIds, isWatched } = useWatchlist()
  const [search, setSearch] = useState('')
  const [status, setStatus] = useState<StatusFilter>('all')
  const [sort, setSort] = useState<SortOption>('trending')
  const [category, setCategory] = useState<CategoryFilter>('all')
  const [watchedOnly, setWatchedOnly] = useState(false)

  const filtered = useMemo(() => {
    const result = allProjects.filter((p) => {
      const matchesStatus = status === 'all' || p.status === status
      const matchesCategory = category === 'all' || p.category === category
      const matchesWatched = !watchedOnly || isWatched(p.id)
      const matchesSearch = p.name.toLowerCase().includes(search.toLowerCase())
        || p.ticker.toLowerCase().includes(search.toLowerCase())
        || p.category.toLowerCase().includes(search.toLowerCase())
      return matchesStatus && matchesCategory && matchesWatched && matchesSearch
    })
    const sorted = [...result]
    if (sort === 'trending')
      sorted.sort((a, b) => b.participants - a.participants)
    else if (sort === 'most-raised')
      sorted.sort((a, b) => b.raised - a.raised)
    else if (sort === 'ending-soon')
      sorted.sort((a, b) => new Date(a.endsAt).getTime() - new Date(b.endsAt).getTime())
    return sorted
  }, [allProjects, status, search, sort, category, watchedOnly, watchIds, isWatched])

  useEffect(() => {
    prefetchProjectLinks(filtered.flatMap(project => [
      project.contractAddress,
      project.shareSlug,
      project.id,
      project.tokenAddress,
    ]))
  }, [filtered])

  return (
    <Layout>
      <SeoHead
        title="Presale — Community Funding Raises"
        description="Browse live, upcoming, and completed presale raises on Penguin Pad. Transparent soft/hard cap goals and contribution tracking."
      />

      <section className="mx-auto max-w-7xl px-4 pb-4 pt-10 sm:px-6 lg:px-8">
        <div className="flex items-center gap-3">
          <div className="flex h-12 w-12 items-center justify-center rounded-2xl border border-orange-400/20 bg-orange-400/10 text-orange-400">
            <TrendingUp className="h-5 w-5" aria-hidden="true" />
          </div>
          <div>
            <p className="text-[11px] font-semibold uppercase tracking-[0.18em] text-orange-400">Community raise</p>
            <h1 className="text-3xl font-bold text-white sm:text-4xl">Presale</h1>
            <p className="mt-1 text-sm text-zinc-400">0.1 ETH to open · 3% of ETH raised · LP 70–100% / creator 0–30%</p>
          </div>
        </div>
        <div className="mt-5">
          <Link
            to="/submit?type=presale"
            className="inline-flex items-center gap-2 rounded-full bg-orange-400 px-4 py-2 text-sm font-semibold text-black transition-colors hover:bg-orange-300"
          >
            <Plus className="h-4 w-4" aria-hidden="true" />
            Create Presale
          </Link>
        </div>
      </section>

      <section className="mx-auto max-w-7xl px-4 py-8 sm:px-6 lg:px-8">
        <div className="rounded-3xl border border-white/10 bg-white/[0.03] p-4 backdrop-blur-md sm:p-5">
        <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
          <div className="relative w-full sm:max-w-xs">
            <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-zinc-500" aria-hidden="true" />
            <input
              type="search"
              value={search}
              onChange={e => setSearch(e.target.value)}
              placeholder="Search projects or ticker"
              aria-label="Search presale projects"
              className="w-full rounded-full border border-white/10 bg-black/40 py-2.5 pl-9 pr-3 text-sm text-white placeholder:text-zinc-500 focus:border-orange-400/50 focus:outline-none focus:ring-1 focus:ring-orange-400/30"
            />
          </div>
          <div className="flex gap-1.5 overflow-x-auto" role="tablist" aria-label="Filter by status">
            {statusFilters.map(f => (
              <button
                key={f.value}
                type="button"
                role="tab"
                aria-selected={status === f.value}
                onClick={() => setStatus(f.value)}
                className={`shrink-0 rounded-full px-3.5 py-2 text-sm font-medium transition-colors ${
                  status === f.value
                    ? 'bg-orange-400 text-black'
                    : 'border border-white/10 text-zinc-400 hover:text-white'
                }`}
              >
                {f.label}
              </button>
            ))}
          </div>
        </div>

        <div className="mt-3 flex flex-wrap items-center gap-1.5" role="tablist" aria-label="Filter by category">
          <button
            type="button"
            role="tab"
            aria-selected={category === 'all'}
            onClick={() => setCategory('all')}
            className={`rounded-full px-3 py-1.5 text-xs font-medium transition-colors ${
              category === 'all'
                ? 'bg-orange-400/15 text-orange-400'
                : 'border border-white/10 text-zinc-500 hover:text-white'
            }`}
          >
            All categories
          </button>
          {projectCategories.map(cat => (
            <button
              key={cat}
              type="button"
              role="tab"
              aria-selected={category === cat}
              onClick={() => setCategory(cat)}
              className={`rounded-full px-3 py-1.5 text-xs font-medium transition-colors ${
                category === cat
                  ? 'bg-orange-400/15 text-orange-400'
                  : 'border border-white/10 text-zinc-500 hover:text-white'
              }`}
            >
              {cat}
            </button>
          ))}
          <button
            type="button"
            aria-pressed={watchedOnly}
            onClick={() => setWatchedOnly(value => !value)}
            className={`ml-auto inline-flex items-center gap-1.5 rounded-full px-3 py-1.5 text-xs font-medium transition-colors ${
              watchedOnly
                ? 'bg-orange-400 text-black'
                : 'border border-white/10 text-zinc-500 hover:text-white'
            }`}
          >
            <Star className={`h-3.5 w-3.5 ${watchedOnly ? 'fill-current' : ''}`} aria-hidden="true" />
            Watched
            {watchIds.length > 0 ? ` (${watchIds.length})` : ''}
          </button>
        </div>

        <div className="mt-3 flex items-center justify-between gap-2">
          <p className="text-xs text-zinc-500">
            {filtered.length}
            {' '}
            {filtered.length === 1 ? 'project' : 'projects'}
          </p>
          <div className="flex items-center gap-2">
            <ArrowDownUp className="h-3.5 w-3.5 text-zinc-500" aria-hidden="true" />
            <label htmlFor="presale-sort" className="text-xs text-zinc-500">Sort by</label>
            <select
              id="presale-sort"
              value={sort}
              onChange={e => setSort(e.target.value as SortOption)}
              className="rounded-full border border-white/10 bg-black/40 px-2.5 py-1.5 text-xs font-medium text-white focus:border-orange-400/50 focus:outline-none focus:ring-1 focus:ring-orange-400/30"
            >
              {sortOptions.map(o => (
                <option key={o.value} value={o.value}>{o.label}</option>
              ))}
            </select>
          </div>
        </div>
        </div>

        {filtered.length > 0
          ? (
              <div className="mt-8 grid grid-cols-2 gap-3 lg:grid-cols-3 lg:gap-5">
                {filtered.map(project => (
                  <ProjectCard key={project.id} project={project} />
                ))}
              </div>
            )
          : (
              <div className="mt-16 flex flex-col items-center text-center">
                <div className="flex h-14 w-14 items-center justify-center rounded-full bg-zinc-900 text-zinc-500">
                  <Search className="h-6 w-6" aria-hidden="true" />
                </div>
                <p className="mt-4 text-sm text-zinc-400">
                  {watchedOnly ? 'No watched raises match these filters. Star a project to keep it here.' : 'No projects match your filters.'}
                </p>
              </div>
            )}
      </section>
    </Layout>
  )
}

export default Presale
